import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { useEditPage } from "@/hooks/useEditPage";
import ErrorBanner from "@/components/common/ErrorBanner";
import { api, type ApiResponse } from "@/api/client";
import { supplierCreateSchema } from "@ckgo2/shared/schemas";
import {
  SUPPLIER_TYPES,
  SUPPLIER_PAYMENT_PROFILES,
  SUPPLIER_TYPE_LABELS,
  SUPPLIER_PAYMENT_PROFILE_LABELS,
  BOOKING_MODES,
  BOOKING_MODE_LABELS,
  type Destination,
  type SupplierType,
  type SupplierPaymentProfile,
  type BookingMode,
} from "@ckgo2/shared/types";

interface FormData {
  name: string;
  type: SupplierType;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  currency: string;
  paymentProfile: SupplierPaymentProfile;
  bookingMode: BookingMode;
  paymentTerms: number | null;
  commissionPct: number | null;
  destinationId: string | null;
  notes: string;
  isActive: boolean;
  emailTemplateSlug: string | null;
}

interface EmailTemplate {
  slug: string;
  name: string;
}

interface Supplier extends FormData {
  id: string;
}

export default function SupplierEditPage() {
  const { data: destData } = useQuery({
    queryKey: ["destinations"],
    queryFn: () => api.get<ApiResponse<Destination[]>>("/api/admin/destinations"),
  });

  // Active email templates for the per-supplier override picker (Block 4.12 Řez 2).
  const { data: tplData } = useQuery({
    queryKey: ["email-templates"],
    queryFn: () =>
      api.get<ApiResponse<EmailTemplate[]>>("/api/admin/templates?type=email&is_active=true"),
  });

  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    FormData,
    Supplier
  >({
    resourceKey: "supplier",
    listKey: "suppliers",
    apiPath: "/api/admin/suppliers",
    schema: supplierCreateSchema,
    defaultValues: {
      name: "",
      type: "hotel",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      currency: "CZK",
      paymentProfile: "invoice",
      bookingMode: "confirm_required",
      paymentTerms: null,
      commissionPct: null,
      destinationId: null,
      notes: "",
      isActive: true,
      emailTemplateSlug: null,
    },
    navigateTo: "/suppliers",
    transformOnLoad: (s) => ({
      name: s.name || "",
      type: s.type || "hotel",
      contactName: s.contactName || "",
      contactEmail: s.contactEmail || "",
      contactPhone: s.contactPhone || "",
      currency: s.currency || "CZK",
      paymentProfile: s.paymentProfile || "invoice",
      bookingMode: s.bookingMode || "confirm_required",
      paymentTerms: s.paymentTerms ?? null,
      commissionPct: s.commissionPct ?? null,
      destinationId: s.destinationId ?? null,
      notes: s.notes || "",
      isActive: s.isActive ?? true,
      emailTemplateSlug: s.emailTemplateSlug ?? null,
    }),
  });

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (!isNew && !isLoading && !itemData) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-lg text-gray-600 dark:text-gray-400">Supplier not found</p>
        <Link to="/suppliers" className="btn-primary">
          <ArrowLeft className="h-5 w-5" />
          Back
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/suppliers" className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700">
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "New supplier" : "Edit supplier"}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew ? "Add a service supplier" : `Editing: ${itemData?.name}`}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
          className="btn-primary"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-5 w-5" />
              Save
            </>
          )}
        </button>
      </div>

      <ErrorBanner error={saveMutation.error} />

      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="card space-y-4">
            <div>
              <label htmlFor="name" className="label">
                Name *
              </label>
              <input
                {...register("name")}
                type="text"
                id="name"
                className="input"
                placeholder="e.g. Sofitel Legend Metropole Hanoi"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="type" className="label">
                  Type
                </label>
                <select {...register("type")} id="type" className="input">
                  {SUPPLIER_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {SUPPLIER_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="destinationId" className="label">
                  Destination
                </label>
                <select {...register("destinationId")} id="destinationId" className="input">
                  <option value="">Global (any destination)</option>
                  {(destData?.data ?? []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label htmlFor="contactName" className="label">
                  Contact name
                </label>
                <input
                  {...register("contactName")}
                  type="text"
                  id="contactName"
                  className="input"
                />
              </div>
              <div>
                <label htmlFor="contactEmail" className="label">
                  Contact email
                </label>
                <input
                  {...register("contactEmail")}
                  type="email"
                  id="contactEmail"
                  className="input"
                />
              </div>
              <div>
                <label htmlFor="contactPhone" className="label">
                  Contact phone
                </label>
                <input
                  {...register("contactPhone")}
                  type="text"
                  id="contactPhone"
                  className="input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="emailTemplateSlug" className="label">
                Email template
              </label>
              <select {...register("emailTemplateSlug")} id="emailTemplateSlug" className="input">
                <option value="">Default (automatic, kind-aware booking request)</option>
                {(tplData?.data ?? []).map((t) => (
                  <option key={t.slug} value={t.slug}>
                    {t.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                Leave on Default unless this supplier insists on a fixed format — then point it at a
                template (clone “Supplier booking request” in Templates and edit it).
              </p>
            </div>

            <div>
              <label htmlFor="notes" className="label">
                Notes
              </label>
              <textarea
                {...register("notes")}
                id="notes"
                rows={3}
                className="input"
                placeholder="Booking contact, contract terms, anything the team should know…"
              />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card space-y-4">
            <div>
              <label htmlFor="currency" className="label">
                Currency
              </label>
              <input
                {...register("currency")}
                type="text"
                id="currency"
                maxLength={3}
                className="input uppercase"
                placeholder="CZK"
              />
            </div>

            <div>
              <label htmlFor="paymentProfile" className="label">
                Payment profile
              </label>
              <select {...register("paymentProfile")} id="paymentProfile" className="input">
                {SUPPLIER_PAYMENT_PROFILES.map((p) => (
                  <option key={p} value={p}>
                    {SUPPLIER_PAYMENT_PROFILE_LABELS[p]}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                Drives which payment obligations the ops engine watches.
              </p>
            </div>

            <div>
              <label htmlFor="bookingMode" className="label">
                Booking mode
              </label>
              <select {...register("bookingMode")} id="bookingMode" className="input">
                {BOOKING_MODES.map((m) => (
                  <option key={m} value={m}>
                    {BOOKING_MODE_LABELS[m]}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                “On demand” = our own / always-available service (our boat): no booking-request
                email, no confirm deadline, ready by default. New catalogue items inherit this.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="paymentTerms" className="label">
                  Payment terms (days)
                </label>
                <input
                  {...register("paymentTerms", { valueAsNumber: true })}
                  type="number"
                  id="paymentTerms"
                  className="input"
                  placeholder="14"
                />
              </div>
              <div>
                <label htmlFor="commissionPct" className="label">
                  Commission (%)
                </label>
                <input
                  {...register("commissionPct", { valueAsNumber: true })}
                  type="number"
                  id="commissionPct"
                  className="input"
                  placeholder="10"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <input
                {...register("isActive")}
                type="checkbox"
                id="isActive"
                className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
              />
              <label
                htmlFor="isActive"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Active
              </label>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
